export default function SalaryDetailsStep({
  formData,
  setFormData,
  attendanceHolidayPolicies = [],
}) {
  const handleChange = (event) => {
    const { name, value, type, checked } = event.target;
    setFormData((previous) => ({
      ...previous,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const selectedPolicy = attendanceHolidayPolicies.find(
    (policy) => policy.id === formData.attendanceHolidayPolicyId,
  );
  const sortedTiers = [...(selectedPolicy?.tiers || [])].sort(
    (left, right) =>
      Number(left.minimumQualifyingDays) - Number(right.minimumQualifyingDays),
  );

  return (
    <div className="space-y-10">
      <section>
        <h3 className="mb-6 text-lg font-semibold text-gray-900">
          Compensation & Payroll Rules
        </h3>
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
          <Field label="Salary Type">
            <Select
              name="salaryType"
              value={formData.salaryType || ""}
              onChange={handleChange}
            >
              <option value="">Select salary type</option>
              <option value="MONTHLY">Monthly</option>
              <option value="DAILY">Daily</option>
            </Select>
          </Field>
          <Field label="Basic Salary">
            <Input
              type="number"
              min="0"
              step="0.01"
              name="basicSalary"
              value={formData.basicSalary ?? ""}
              onChange={handleChange}
              placeholder="25000"
            />
          </Field>
          <Field label="Annual CTC (optional)">
            <Input type="number" min="0" step="0.01" name="ctc" value={formData.ctc ?? ""} onChange={handleChange} placeholder="Leave blank if not configured" />
          </Field>
          <ToggleCard
            id="overtimeEligible"
            name="overtimeEligible"
            checked={Boolean(formData.overtimeEligible)}
            onChange={handleChange}
            title="Eligible for overtime pay"
            description="Pay overtime only when the employee works beyond their configured shift."
          />
          <Field
            label="Custom Overtime Rate (₹ / hour)"
            hint="Optional. When blank, payroll derives the rate from salary and shift duration."
          >
            <Input
              type="number"
              min="0"
              step="0.01"
              name="overtimeRate"
              value={formData.overtimeRate ?? ""}
              onChange={handleChange}
              disabled={!formData.overtimeEligible}
              placeholder="250"
            />
          </Field>
          <ToggleCard
            id="earlyExitDeductionEligible"
            name="earlyExitDeductionEligible"
            checked={Boolean(formData.earlyExitDeductionEligible)}
            onChange={handleChange}
            title="Deduct for early exit / undertime"
            description="Deduct only when this employee should lose pay for unworked shift minutes."
          />
          <Field
            label="Custom Early-Exit Rate (₹ / minute)"
            hint="Optional. When blank, payroll derives the minute rate from salary and shift duration."
          >
            <Input
              type="number"
              min="0"
              step="0.01"
              name="earlyExitDeductionRate"
              value={formData.earlyExitDeductionRate ?? ""}
              onChange={handleChange}
              disabled={!formData.earlyExitDeductionEligible}
              placeholder="5"
            />
          </Field>
          <Field
            label="POS Commission (%)"
            hint="Optional. Percentage earned on eligible POS sales."
          >
            <Input
              type="number"
              min="0"
              max="100"
              step="0.01"
              name="commissionPercent"
              value={formData.commissionPercent ?? ""}
              onChange={handleChange}
              placeholder="Example: 5"
            />
          </Field>
          <Field label="Grace Period (Minutes)">
            <Input
              type="number"
              min="0"
              name="graceMinutes"
              value={formData.graceMinutes ?? ""}
              onChange={handleChange}
              placeholder="15"
            />
          </Field>
        </div>
      </section>

      <section>
        <div className="mb-6">
          <h3 className="text-lg font-semibold text-gray-900">
            Shift Configuration
          </h3>
          <p className="mt-1 text-sm text-gray-500">
            Payroll compares actual check-in and check-out time against this
            schedule minute by minute.
          </p>
        </div>
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
          <Field label="Shift Type">
            <Select
              name="shiftType"
              value={formData.shiftType || ""}
              onChange={handleChange}
            >
              <option value="">Select shift</option>
              <option value="General">General</option>
              <option value="Morning">Morning</option>
              <option value="Evening">Evening</option>
              <option value="Night">Night</option>
            </Select>
          </Field>
          <Field label="Break Duration (Minutes)">
            <Input
              type="number"
              min="0"
              name="breakDurationMinutes"
              value={formData.breakDurationMinutes ?? ""}
              onChange={handleChange}
              placeholder="60"
            />
          </Field>
          <Field label="Shift Start Time">
            <Input
              type="time"
              name="shiftStartTime"
              value={formData.shiftStartTime || ""}
              onChange={handleChange}
            />
          </Field>
          <Field label="Shift End Time">
            <Input
              type="time"
              name="shiftEndTime"
              value={formData.shiftEndTime || ""}
              onChange={handleChange}
            />
          </Field>
        </div>
      </section>

      <section className="rounded-2xl border border-primary-100 bg-primary-50/40 p-6">
        <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.14em] text-primary-700">
              Attendance entitlement
            </p>
            <h3 className="mt-1 text-lg font-semibold text-gray-900">
              Leave & Holiday Policy
            </h3>
            <p className="mt-1 max-w-2xl text-sm leading-6 text-gray-600">
              The policy controls how qualifying attendance progressively
              unlocks paid holiday allowance. It replaces the old per-employee
              monthly holiday count.
            </p>
          </div>
          {selectedPolicy ? (
            <span className="w-fit rounded-full bg-white px-3 py-1 text-xs font-semibold text-primary-800 shadow-sm">
              Active policy selected
            </span>
          ) : null}
        </div>
        <div className="mt-6 grid grid-cols-1 gap-5 md:grid-cols-2">
          <Field
            label="Attendance / Holiday Policy"
            hint="Optional. Without a policy, absences are unpaid unless another approved leave rule applies."
          >
            <Select
              name="attendanceHolidayPolicyId"
              value={formData.attendanceHolidayPolicyId || ""}
              onChange={handleChange}
            >
              <option value="">No progressive holiday policy</option>
              {attendanceHolidayPolicies.map((policy) => (
                <option key={policy.id} value={policy.id}>
                  {policy.name} ({policy.code})
                </option>
              ))}
            </Select>
          </Field>
          <Field
            label="Policy Effective From"
            hint="Normally use the joining date. Future changes create a new effective-dated assignment."
          >
            <Input
              type="date"
              name="attendanceHolidayPolicyEffectiveFrom"
              value={formData.attendanceHolidayPolicyEffectiveFrom || ""}
              onChange={handleChange}
              disabled={!formData.attendanceHolidayPolicyId}
            />
          </Field>
        </div>
        {selectedPolicy ? (
          <div className="mt-5 rounded-xl border border-primary-100 bg-white p-4">
            <p className="text-sm font-semibold text-gray-800">
              {selectedPolicy.name}
            </p>
            {selectedPolicy.description ? (
              <p className="mt-1 text-sm text-gray-500">
                {selectedPolicy.description}
              </p>
            ) : null}
            <p className="mt-3 text-xs font-medium text-gray-500">
              Qualifying days: Present and late = 1 day; half day = 0.5 day
              {selectedPolicy.countPaidLeaveAsQualifying
                ? "; approved paid leave also counts."
                : "."}
            </p>
            {sortedTiers.length ? (
              <div className="mt-4 flex flex-wrap gap-2">
                {sortedTiers.map((tier) => (
                  <span
                    key={
                      tier.id ||
                      `${tier.minimumQualifyingDays}-${tier.holidayDays}`
                    }
                    className="rounded-lg bg-primary-50 px-3 py-2 text-xs font-semibold text-primary-800"
                  >
                    {tier.minimumQualifyingDays} qualifying days →{" "}
                    {tier.holidayDays} paid holiday
                    {Number(tier.holidayDays) === 1 ? "" : "s"}
                  </span>
                ))}
              </div>
            ) : null}
          </div>
        ) : null}
        {!attendanceHolidayPolicies.length ? (
          <p className="mt-4 text-sm text-amber-700">
            No active policy exists yet. Create one from Leave & Holiday
            Policies, then return here to assign it.
          </p>
        ) : null}
      </section>

      <section>
        <h3 className="mb-6 text-lg font-semibold text-gray-900">
          Payment & Bank Details
        </h3>
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
          <Field label="Payment Mode">
            <Select
              name="paymentMode"
              value={formData.paymentMode || ""}
              onChange={handleChange}
            >
              <option value="">Select payment mode</option>
              <option value="BANK_TRANSFER">Bank Transfer</option>
              <option value="CASH">Cash</option>
              <option value="CHEQUE">Cheque</option>
            </Select>
          </Field>
          <Field label="Bank Name">
            <Input
              type="text"
              name="bankName"
              value={formData.bankName || ""}
              onChange={handleChange}
              placeholder="State Bank of India"
            />
          </Field>
          <Field label="Account Holder Name">
            <Input
              type="text"
              name="accountHolderName"
              value={formData.accountHolderName || ""}
              onChange={handleChange}
              placeholder="Rahul Sharma"
            />
          </Field>
          <Field label="Account Number">
            <Input
              type="text"
              name="accountNumber"
              value={formData.accountNumber || ""}
              onChange={handleChange}
              placeholder="1234567890"
            />
          </Field>
          <Field label="IFSC Code">
            <Input
              type="text"
              name="ifscCode"
              value={formData.ifscCode || ""}
              onChange={handleChange}
              placeholder="SBIN0001234"
            />
          </Field>
        </div>
      </section>
    </div>
  );
}

function Input({ className = "", ...props }) {
  return (
    <input
      {...props}
      className={`w-full rounded-xl border border-gray-200 bg-white px-4 py-3 outline-none transition focus:border-primary-500 focus:ring-2 focus:ring-primary-200 disabled:cursor-not-allowed disabled:bg-gray-100 disabled:text-gray-500 ${className}`}
    />
  );
}
function Select({ className = "", children, ...props }) {
  return (
    <select
      {...props}
      className={`w-full rounded-xl border border-gray-200 bg-white px-4 py-3 outline-none transition focus:border-primary-500 focus:ring-2 focus:ring-primary-200 disabled:cursor-not-allowed disabled:bg-gray-100 ${className}`}
    >
      {children}
    </select>
  );
}
function Field({ label, hint, children }) {
  return (
    <div>
      <label className="mb-2 block text-sm font-medium text-gray-700">
        {label}
      </label>
      {children}
      {hint ? <p className="mt-1 text-xs text-gray-500">{hint}</p> : null}
    </div>
  );
}
function ToggleCard({ id, name, checked, onChange, title, description }) {
  return (
    <label
      htmlFor={id}
      className="flex cursor-pointer items-start gap-3 rounded-xl border border-gray-200 p-4 transition hover:bg-gray-50"
    >
      <input
        id={id}
        type="checkbox"
        name={name}
        checked={checked}
        onChange={onChange}
        className="mt-1 h-4 w-4 accent-primary-700"
      />
      <span>
        <span className="block text-sm font-semibold text-gray-800">
          {title}
        </span>
        <span className="mt-1 block text-xs leading-5 text-gray-500">
          {description}
        </span>
      </span>
    </label>
  );
}
